// src/auth/OtpForm.jsx
// Two-step email OTP sign-in form, shared by LoginModal and LoginPage.
import React, { useEffect, useState } from "react";
import { useAuth } from "./AuthContext";
import "./OtpForm.css";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function formatWait(seconds) {
  if (seconds < 60) return `${seconds} second${seconds === 1 ? "" : "s"}`;
  const mins = Math.ceil(seconds / 60);
  return `${mins} minute${mins === 1 ? "" : "s"}`;
}

// Supabase returns 429 for two very different email limits, and the flat
// "email rate limit exceeded" text is useless to a user. Split them apart:
//
//   1. Per-address cooldown — message carries the exact seconds remaining
//      ("For security purposes, you can only request this after 47 seconds.").
//      We parse it and run a real countdown.
//   2. Project-wide hourly send cap — no reset time is returned by the API, so
//      we say so plainly rather than invent a countdown we can't compute.
//
// Returns { message, retryAfter } where retryAfter is seconds, or 0 if unknown.
function describeSendError(err) {
  const raw = (err && err.message) || "";
  const status = err && err.status;
  const isRateLimit =
    status === 429 ||
    (err && err.code === "over_email_send_rate_limit") ||
    /rate limit/i.test(raw);

  if (!isRateLimit) {
    return { message: raw || "Could not send the code. Please try again.", retryAfter: 0 };
  }

  const match = raw.match(/after (\d+) seconds?/i);
  if (match) {
    const seconds = parseInt(match[1], 10);
    return {
      message: `A code was just sent to this address. You can request another in ${formatWait(seconds)}.`,
      retryAfter: seconds,
    };
  }

  return {
    message:
      "This site has hit its hourly limit for sign-in emails — that cap is shared " +
      "across everyone, so switching to a different email address won't help. " +
      "Please try again later (the limit resets within the hour). Any code you " +
      "already received is still valid.",
    retryAfter: 0,
  };
}

function OtpForm({ onSuccess }) {
  const { signInWithEmail, verifyCode } = useAuth();

  const [step, setStep] = useState("email"); // 'email' | 'code'
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const timer = setTimeout(() => setCooldown((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  const sendCode = async () => {
    const trimmed = email.trim();
    if (!EMAIL_RE.test(trimmed)) {
      setError("Please enter a valid email address.");
      return false;
    }
    setBusy(true);
    setError("");
    const { error: sendError } = await signInWithEmail(trimmed);
    setBusy(false);
    if (sendError) {
      const { message, retryAfter } = describeSendError(sendError);
      setError(message);
      // Drive the button countdown off the server's own number when it gave
      // us one, so we never invite a retry the API will just reject again.
      if (retryAfter > 0) setCooldown(retryAfter);
      return false;
    }
    setCooldown(60);
    return true;
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (busy || cooldown > 0) return;
    const ok = await sendCode();
    if (ok) {
      setCode("");
      setStep("code");
    }
  };

  const handleResend = async () => {
    if (busy || cooldown > 0) return;
    await sendCode();
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    if (busy) return;
    const trimmedCode = code.trim();
    if (!/^\d{6}$/.test(trimmedCode)) {
      setError("Enter the 6-digit code from your email.");
      return;
    }
    setBusy(true);
    setError("");
    const { error: verifyError } = await verifyCode(email.trim(), trimmedCode);
    setBusy(false);
    if (verifyError) {
      setError(verifyError.message || "That code didn't work. Please try again.");
      return;
    }
    if (onSuccess) onSuccess();
  };

  const handleBack = () => {
    setStep("email");
    setCode("");
    setError("");
  };

  if (step === "email") {
    return (
      <form className="otp-form" onSubmit={handleEmailSubmit}>
        <p className="otp-copy">
          We'll email you a 6-digit sign-in code. No password needed.
        </p>
        <label className="otp-label" htmlFor="otp-email">
          Email
        </label>
        <input
          id="otp-email"
          className="otp-input"
          type="email"
          autoComplete="email"
          placeholder="you@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={busy}
          autoFocus
        />
        {error && <p className="otp-error">{error}</p>}
        <button
          className="otp-primary"
          type="submit"
          disabled={busy || cooldown > 0}
        >
          {busy
            ? "Sending…"
            : cooldown > 0
            ? `Try again in ${cooldown}s`
            : "Send code"}
        </button>
        {error && EMAIL_RE.test(email.trim()) && (
          // A rate-limited send doesn't invalidate a code they already have,
          // so give them a way to the verify step instead of a dead end.
          <div className="otp-secondary-row">
            <button
              type="button"
              className="otp-link"
              onClick={() => {
                setCode("");
                setError("");
                setStep("code");
              }}
            >
              I already have a code
            </button>
          </div>
        )}
      </form>
    );
  }

  return (
    <form className="otp-form" onSubmit={handleVerify}>
      <p className="otp-copy">
        We sent a 6-digit code to <strong>{email.trim()}</strong>. Enter it
        below to sign in. If your email shows a sign-in link instead, clicking
        the link also signs you in.
      </p>
      <label className="otp-label" htmlFor="otp-code">
        Sign-in code
      </label>
      <input
        id="otp-code"
        className="otp-input otp-code-input"
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        maxLength={6}
        autoComplete="one-time-code"
        placeholder="123456"
        value={code}
        onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
        disabled={busy}
        autoFocus
      />
      {error && <p className="otp-error">{error}</p>}
      <button
        className="otp-primary"
        type="submit"
        disabled={busy || code.length !== 6}
      >
        {busy ? "Verifying…" : "Verify"}
      </button>
      <div className="otp-secondary-row">
        <button
          type="button"
          className="otp-link"
          onClick={handleResend}
          disabled={busy || cooldown > 0}
        >
          {cooldown > 0 ? `Resend code in ${cooldown}s` : "Resend code"}
        </button>
        <button
          type="button"
          className="otp-link"
          onClick={handleBack}
          disabled={busy}
        >
          Use a different email
        </button>
      </div>
    </form>
  );
}

export default OtpForm;
